import { memo, useState, useCallback, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUtensils,
  faExternalLinkAlt,
  faMapMarkerAlt,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";

const OpenTableConfigForm = memo(({ config, onUpdate }) => {
  const [formData, setFormData] = useState({
    openTableUrl: "",
    restaurantName: "",
    cuisineType: "",
    location: "",
    description: "",
    showDescription: true,
    showCuisine: true,
    showLocation: true,
    buttonText: "Make Reservation",
    buttonStyle: "primary",
    ...config,
  });

  const [urlError, setUrlError] = useState("");
  const [isValidating, setIsValidating] = useState(false);

  // Validate OpenTable URL
  const validateOpenTableUrl = useCallback((url) => {
    if (!url) return { valid: false, message: "" };

    try {
      const urlObj = new URL(url);
      const isValid =
        urlObj.hostname === "www.opentable.com" ||
        urlObj.hostname === "opentable.com" ||
        urlObj.hostname.includes("opentable.");

      if (!isValid) {
        return {
          valid: false,
          message: "Please enter a valid OpenTable restaurant URL",
        };
      }

      return { valid: true, message: "" };
    } catch (e) {
      return {
        valid: false,
        message: "Please enter a valid URL",
      };
    }
  }, []);

  // Handle URL input change with validation
  const handleUrlChange = useCallback(
    (e) => {
      const url = e.target.value;
      setFormData((prev) => ({ ...prev, openTableUrl: url }));

      if (url) {
        setIsValidating(true);
        const validation = validateOpenTableUrl(url);
        setUrlError(validation.message);
        setIsValidating(false);
      } else {
        setUrlError("");
      }
    },
    [validateOpenTableUrl]
  );

  // Handle form field changes
  const handleInputChange = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Handle switch changes
  const handleSwitchChange = useCallback((field, checked) => {
    setFormData((prev) => ({
      ...prev,
      [field]: checked,
    }));
  }, []);

  // Submit form data
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      if (!formData.openTableUrl) {
        setUrlError("OpenTable URL is required");
        return;
      }

      const validation = validateOpenTableUrl(formData.openTableUrl);
      if (!validation.valid) {
        setUrlError(validation.message);
        return;
      }

      // Update the widget config
      onUpdate(formData);
    },
    [formData, validateOpenTableUrl, onUpdate]
  );

  // Auto-save on changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        formData.openTableUrl &&
        validateOpenTableUrl(formData.openTableUrl).valid
      ) {
        onUpdate(formData);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [formData, validateOpenTableUrl, onUpdate]);

  // Stop propagation for all interactive elements
  const stopPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Render action buttons in the fixed action area
  useEffect(() => {
    const actionArea = document.getElementById("widget-settings-actions");
    if (!actionArea) return;

    // Create a container for the button
    const buttonContainer = document.createElement("div");
    buttonContainer.className = "w-full";
    actionArea.appendChild(buttonContainer);

    buttonContainer.innerHTML = `
      <button
        type="button"
        class="w-full px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
        id="opentable-submit-btn"
      >
        <svg class="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
        Save OpenTable Widget
      </button>
    `;

    // Add click handler
    const btn = buttonContainer.querySelector("#opentable-submit-btn");
    if (btn) {
      btn.addEventListener("click", handleSubmit);
    }

    // Cleanup function
    return () => {
      if (actionArea && buttonContainer) {
        actionArea.removeChild(buttonContainer);
      }
    };
  }, [handleSubmit]);

  return (
    <div className="w-full" onClick={stopPropagation}>
      <div className="space-y-6">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center gap-3 pb-4 border-b border-white/10">
            <FontAwesomeIcon
              icon={faUtensils}
              className="w-5 h-5 text-red-500"
            />
            <div>
              <h3 className="text-lg font-semibold text-white">
                OpenTable Integration
              </h3>
              <p className="text-sm text-white/60">
                Connect your restaurant's OpenTable page
              </p>
            </div>
          </div>

          {/* OpenTable URL */}
          <div>
            <Label htmlFor="openTableUrl" className="text-white mb-2 block">
              OpenTable Restaurant URL *
            </Label>
            <Input
              id="openTableUrl"
              type="url"
              value={formData.openTableUrl}
              onChange={handleUrlChange}
              placeholder="https://www.opentable.com/your-restaurant"
              className="bg-white/5 border-white/10 text-white placeholder-white/50 break-all whitespace-normal"
              style={{
                wordBreak: "break-all",
                whiteSpace: "normal",
                lineHeight: "1.4",
                minHeight: "2.5rem",
                height: "auto",
                padding: "0.75rem",
              }}
              onClick={stopPropagation}
            />
            {urlError && (
              <p className="text-red-400 text-sm mt-1">{urlError}</p>
            )}
            {isValidating && (
              <p className="text-blue-400 text-sm mt-1">Validating URL...</p>
            )}
            <p className="text-white/50 text-xs mt-1">
              Copy the URL from your restaurant's OpenTable page
            </p>
          </div>

          {/* Restaurant Information */}
          <div className="space-y-4 pt-4 border-t border-white/10">
            <h4 className="text-sm font-medium text-white/80">
              Restaurant Information
            </h4>

            {/* Restaurant Name */}
            <div>
              <Label htmlFor="restaurantName" className="text-white mb-2 block">
                Restaurant Name
              </Label>
              <Input
                id="restaurantName"
                type="text"
                value={formData.restaurantName}
                onChange={(e) =>
                  handleInputChange("restaurantName", e.target.value)
                }
                placeholder="Your Restaurant Name"
                className="bg-white/5 border-white/10 text-white placeholder-white/50"
                onClick={stopPropagation}
              />
              <p className="text-white/50 text-xs mt-1">
                Leave empty to auto-extract from URL
              </p>
            </div>

            {/* Cuisine Type */}
            <div>
              <Label htmlFor="cuisineType" className="text-white mb-2 block">
                Cuisine Type
              </Label>
              <Input
                id="cuisineType"
                type="text"
                value={formData.cuisineType}
                onChange={(e) =>
                  handleInputChange("cuisineType", e.target.value)
                }
                placeholder="Italian, American, Asian Fusion, etc."
                className="bg-white/5 border-white/10 text-white placeholder-white/50"
                onClick={stopPropagation}
              />
            </div>

            {/* Location */}
            <div>
              <Label htmlFor="location" className="text-white mb-2 block">
                Location
              </Label>
              <Input
                id="location"
                type="text"
                value={formData.location}
                onChange={(e) => handleInputChange("location", e.target.value)}
                placeholder="New York, NY"
                className="bg-white/5 border-white/10 text-white placeholder-white/50"
                onClick={stopPropagation}
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description" className="text-white mb-2 block">
                Description
              </Label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder="Brief description of your restaurant..."
                className="w-full bg-white/5 border border-white/10 text-white placeholder-white/50 rounded-md px-3 py-2 min-h-[80px] resize-none"
                onClick={stopPropagation}
              />
            </div>
          </div>

          {/* Display Options */}
          <div className="space-y-4 pt-4 border-t border-white/10">
            <h4 className="text-sm font-medium text-white/80">
              Display Options
            </h4>

            {/* Show Description */}
            <div
              className="flex items-center justify-between"
              onClick={stopPropagation}
            >
              <Label htmlFor="showDescription" className="text-white text-sm">
                Show Description
              </Label>
              <Switch
                id="showDescription"
                checked={formData.showDescription}
                onCheckedChange={(checked) =>
                  handleSwitchChange("showDescription", checked)
                }
                onClick={stopPropagation}
              />
            </div>

            {/* Show Cuisine */}
            <div
              className="flex items-center justify-between"
              onClick={stopPropagation}
            >
              <Label htmlFor="showCuisine" className="text-white text-sm">
                Show Cuisine Type
              </Label>
              <Switch
                id="showCuisine"
                checked={formData.showCuisine}
                onCheckedChange={(checked) =>
                  handleSwitchChange("showCuisine", checked)
                }
                onClick={stopPropagation}
              />
            </div>

            {/* Show Location */}
            <div
              className="flex items-center justify-between"
              onClick={stopPropagation}
            >
              <Label htmlFor="showLocation" className="text-white text-sm">
                Show Location
              </Label>
              <Switch
                id="showLocation"
                checked={formData.showLocation}
                onCheckedChange={(checked) =>
                  handleSwitchChange("showLocation", checked)
                }
                onClick={stopPropagation}
              />
            </div>
          </div>

          {/* Button Customization */}
          <div className="space-y-4 pt-4 border-t border-white/10">
            <h4 className="text-sm font-medium text-white/80">
              Button Customization
            </h4>

            {/* Button Text */}
            <div>
              <Label htmlFor="buttonText" className="text-white mb-2 block">
                Button Text
              </Label>
              <Input
                id="buttonText"
                type="text"
                value={formData.buttonText}
                onChange={(e) =>
                  handleInputChange("buttonText", e.target.value)
                }
                placeholder="Make Reservation"
                className="bg-white/5 border-white/10 text-white placeholder-white/50"
                onClick={stopPropagation}
              />
            </div>

            {/* Button Style */}
            <div>
              <Label htmlFor="buttonStyle" className="text-white mb-2 block">
                Button Style
              </Label>
              <select
                id="buttonStyle"
                value={formData.buttonStyle}
                onChange={(e) =>
                  handleInputChange("buttonStyle", e.target.value)
                }
                className="w-full bg-white/5 border border-white/10 text-white rounded-md px-3 py-2"
                onClick={stopPropagation}
              >
                <option value="primary">Primary (Red)</option>
                <option value="secondary">Secondary (Gray)</option>
                <option value="outline">Outline</option>
              </select>
            </div>
          </div>

          {/* Info section */}
          <div className="text-xs text-white/60 bg-red-500/10 border border-red-500/20 p-3 rounded-lg">
            <p className="font-medium mb-2 text-red-400 flex items-center gap-2">
              <FontAwesomeIcon icon={faInfoCircle} />
              About OpenTable Integration:
            </p>
            <p>
              This widget creates a reservation button that links to your
              restaurant's OpenTable page. Customers will be redirected to
              OpenTable to complete their reservation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

OpenTableConfigForm.displayName = "OpenTableConfigForm";

export default OpenTableConfigForm;
